import { PortableText } from '@/app/components/questionnaireV2/components/portable-text/portable-text';
import { motion } from 'motion/react';
import { memo } from 'react';
import { CategoryCardBaseProps } from './types';

export const CategoryCardBase = memo(function CategoryCardBase({
  name,
  description,
  icon,
  progressValue,
  progressMax,
  lastUpdated,
  isComplete,
  children,
}: CategoryCardBaseProps) {
  const progressPercentage = (progressValue / progressMax) * 100;

  return (
    <div className="dashboard-reset box-border flex w-full flex-col items-start gap-[9px] rounded-[10px] bg-[#f0f5f6] px-0 pt-0 pb-[35px] lg:max-w-none">
      {/* Header with Icon and Status Tags */}
      <div className="box-border flex h-[60px] w-full items-center justify-between py-0 pr-[20px] pl-0">
        {/* Icon Container */}
        <div className="box-border flex size-[60px] items-center justify-center gap-[10px] rounded-tl-[10px] rounded-br-[10px] border border-solid border-[#d4c2b5] bg-white p-[10px]">
          <div className="flex h-[30px] w-[30px] items-center justify-center">
            <img alt={`${name} icon`} className="object-contain" src={icon} />
          </div>
        </div>

        {/* Status Tags */}
        <div className="flex items-center gap-[5px]">
          {/* Last Updated Tag with conditional styling */}
          {(lastUpdated || !isComplete) && (
            <div
              className={`box-border flex min-h-[25px] items-center justify-center gap-[2px] rounded-[4px] px-[10px] ${
                isComplete ? 'bg-white' : 'bg-[#f5e695]'
              }`}
            >
              <p className="text-center text-[12px] font-semibold text-[#183344]">
                {!isComplete ? `Last Updated ${lastUpdated} - Incomplete` : `Last Updated ${lastUpdated}`}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Body Content */}
      <div className="box-border flex w-full flex-1 flex-col items-center gap-[10px] px-[20px] py-[5px] lg:gap-[14px] lg:rounded-[10px] lg:bg-gradient-to-r lg:from-[#f0f5f6] lg:to-[#f0f5f6] lg:px-[64px] lg:py-0">
        {/* Title and Progress Section */}
        <div className="flex w-full flex-col items-start gap-[7px]">
          {/* Title */}
          <h3 className="w-full text-[22px] leading-[1.3] font-bold text-[#183344]">{name}</h3>

          {/* Progress Section */}
          <div className="flex w-full flex-col items-start gap-[4px]">
            {/* Progress Bar */}
            <div
              className="w-full lg:w-[295px]"
              role="progressbar"
              aria-valuenow={progressValue}
              aria-valuemin={0}
              aria-valuemax={progressMax}
              aria-label={`Progress: ${progressValue} of ${progressMax} factors analyzed`}
              aria-valuetext={`${progressValue} of ${progressMax} factors analyzed`}
            >
              {/* Background Bar */}
              <div className="h-[10px] w-full rounded-[7.451px] bg-[#151a20] opacity-50" />
              {/* Progress Fill */}
              <motion.div
                className="relative -mt-[10px] h-[10px] rounded-[7.451px] bg-[#afdfff]"
                initial={{ width: 0 }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
            </div>

            {/* Progress Text */}
            <div className="flex w-full items-center gap-[10px]">
              <p className="text-[12px] leading-[1.3] font-bold text-[#183344]" aria-hidden="true">
                {progressValue}/{progressMax} Factors Analyzed
              </p>
            </div>
          </div>
        </div>

        {/* Description and Action Section */}
        <div className="flex w-full flex-col items-start gap-[24px] lg:items-center">
          <div className="flex w-full items-center justify-center gap-[10px]">
            <div className="w-full text-left text-[14px] leading-[1.4] font-normal text-[#183344] lg:min-h-px lg:min-w-px lg:grow lg:basis-0">
              <PortableText content={description} />
            </div>
          </div>

          {/* Custom content slot (buttons, expanded content, etc.) */}
          {children}
        </div>
      </div>
    </div>
  );
});

export default CategoryCardBase;
